import React, {useCallback, useRef, useEffect, useMemo} from 'react';
import {debounce} from 'ts-debounce';

import {RealtimeMessage} from 'realtime/RealtimeChannel';
import useRealtimeChannelSubscription, {MessageFilter} from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId, isCurrentClient} from 'realtime/util/RealtimeUtil';

export const HALF_SEC = 500;
export const FIVE_SECS = 5000;

/**
 * Delay values used by `useRealtimeRefetch`. Tests stub these instead of sleeping on wall-clock debounce /
 * insurance timers (RTL tests cannot use fake timers).
 */
export const realtimeRefetchTiming = {
  debounceMs: HALF_SEC,
  maxWaitMs: FIVE_SECS,
  insuranceRefetchMs: FIVE_SECS,
};

export interface UseRealtimeRefetchOptions {
  /**
   * The API ID to subscribe to for realtime updates.
   */
  apiId: string;
  /**
   * Function to call when a realtime update is received.
   * Typically this triggers a GraphQL refetch.
   */
  refetch: () => void;
  /**
   * Filter for which messages to listen to.
   * Use 'no_filter' to receive all messages on the channel.
   */
  messageFilter: MessageFilter;
  /**
   * If true, will refetch even when receiving messages which originated from the same client.
   * This is useful for managing updates to sub-tabs which may be affected by actions from
   * another tab but are difficult to update via the GQL cache.
   */
  refetchOnMessagesFromSameClient?: boolean;
  /**
   * Tells this hook whether to enqueue a refetch in five seconds.
   * Only one refetch will be enqueued at a time.
   *
   * This is to work around the following issue with Apollo:
   * - This hook receives a message and tells Apollo to refetch X_QUERY
   * - Apollo fires X_QUERY, but this query takes a few seconds
   * - This hook receives another message and tells Apollo to refetch X_QUERY
   * - Because Apollo detects X_QUERY is in progress, it doesn't enqueue a second X_QUERY request
   * - But the server may return stale data for X_QUERY
   *
   * If there are no more messages coming on this channel, the client will get "stuck" and never
   * refetch the latest data until the page is refreshed.
   *
   * This prop lets the calling code tell the hook to enqueue a final refetch.
   * (Which means the calling code needs to be able to determine if the data is incomplete or not.)
   */
  hasIncompleteDataShouldRefetch?: boolean;
  /**
   * If true, disables the subscription and refetch behavior.
   */
  disabled?: boolean;
}

interface UseRealtimeChannelRefetchOptions extends Omit<UseRealtimeRefetchOptions, 'apiId'> {
  channelName: string;
}

/**
 * Hook that subscribes to a realtime channel and triggers refetch when updates are received.
 *
 * This hook handles:
 * - Debounced refetching (500ms debounce, 5s max wait) to prevent Apollo query collisions
 * - Same-client filtering option to avoid refetching on own actions
 * - Insurance refetch for cases where Apollo skips queries that are in-flight
 */
export function useRealtimeChannelRefetch({
  channelName,
  refetch,
  messageFilter,
  refetchOnMessagesFromSameClient = false,
  hasIncompleteDataShouldRefetch = false,
  disabled = false,
}: UseRealtimeChannelRefetchOptions): void {
  // We're going to debounce our refetch. However, the refetch prop
  // can change on rerenders so we can't just `debounce(refetch)`.
  // Instead, we need to keep a ref for the current value of refetch,
  // so we can call it from our debounced function.
  const refetchRef = useRef<typeof refetch>(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const tryInvokeRefetch = useCallback((): void => {
    if (refetchRef.current != null) {
      refetchRef.current();
    }
  }, []);

  const finalRefetchTimeoutId = useRef<ReturnType<typeof setTimeout>>();
  const clearFinalRefetchTimeoutId = useCallback((): void => {
    if (finalRefetchTimeoutId.current != null) {
      clearTimeout(finalRefetchTimeoutId.current);
    }
  }, []);

  // Clear timeout on unmount
  useEffect(
    () => () => {
      clearFinalRefetchTimeoutId();
    },
    [clearFinalRefetchTimeoutId]
  );

  // The debounced function also has to live within a useMemo, so that it maintains its state
  // across renders
  const refetcher = useMemo(
    () =>
      // Debouncing helps (1) reduce the number of calls we make to the DB and (2) prevents multiple refetches from
      // "colliding" if the client receives multiple messages near the same time.
      // https://github.com/apollographql/apollo-client/issues/9308
      debounce(
        () => {
          tryInvokeRefetch();
        },
        realtimeRefetchTiming.debounceMs,
        {maxWait: realtimeRefetchTiming.maxWaitMs, isImmediate: false}
      ),
    [tryInvokeRefetch]
  );

  // Enqueue an additional refetch for insurance. See option description.
  // Guard against racing with 1) task completion response and 2) overlapping refreshes so that they do not
  // clobber each other with stale data.
  const prevHasIncompleteDataShouldRefetchRef = useRef(hasIncompleteDataShouldRefetch);
  useEffect(() => {
    const mappingJustCompleted =
      prevHasIncompleteDataShouldRefetchRef.current && !hasIncompleteDataShouldRefetch;

    clearFinalRefetchTimeoutId();
    if (hasIncompleteDataShouldRefetch) {
      finalRefetchTimeoutId.current = setTimeout(() => {
        tryInvokeRefetch();
      }, realtimeRefetchTiming.insuranceRefetchMs);
    } else if (mappingJustCompleted) {
      tryInvokeRefetch();
    }

    prevHasIncompleteDataShouldRefetchRef.current = hasIncompleteDataShouldRefetch;
  }, [hasIncompleteDataShouldRefetch, clearFinalRefetchTimeoutId, tryInvokeRefetch]);

  const onMessage = useCallback(
    (message: RealtimeMessage) => {
      if (!disabled) {
        let shouldRefetch = true;
        if (!refetchOnMessagesFromSameClient) {
          // avoid refetching if it is the same client
          // Type guard: check if the data has clientId property
          if ('clientId' in message.data && typeof message.data.clientId === 'string') {
            shouldRefetch = !isCurrentClient(message.data.clientId);
          }
        }

        if (shouldRefetch) {
          refetcher();
        }
      }
    },
    [disabled, refetcher, refetchOnMessagesFromSameClient]
  );

  useRealtimeChannelSubscription(!disabled ? channelName : '', onMessage, messageFilter);
}

/**
 * Subscribe to an API resource's realtime channel and trigger refetches.
 *
 * See {@link useRealtimeChannelRefetch} for refetch and debounce behavior.
 */
export function useRealtimeRefetch({apiId, ...options}: UseRealtimeRefetchOptions): void {
  useRealtimeChannelRefetch({
    ...options,
    channelName: options.disabled ? '' : getRealtimeChannelNameForApiId(apiId),
  });
}

export interface RealtimeUpdateSubscriptionWrapperProps {
  apiId?: string;
  messageFilter: MessageFilter;
  refetch: () => unknown;
  children: JSX.Element | null;
  /**
   * Will refresh even when receiving messages which originated from the same client.
   * This is useful for managing updates to sub-tabs which may be affected by actions from another tab but are
   * difficult to update via the GQL cache.
   */
  refetchOnMessagesFromSameClient?: boolean;
  /**
   * Tells this wrapper whether to enqueue a refetch in five seconds upon rendering. Only one refetch will be
   * enqueued at a time, since the component maintains internal state.
   *
   * This is to work around the following issue with apollo:
   * - This component receives a message and tells apollo to refetch X_QUERY
   * - Apollo fires X_QUERY, but this query takes a few seconds
   * - This component receives another message and tells apollo to refetch X_QUERY
   * - Because apollo detects X_QUERY is in progress, it doesn't enqueue a second X_QUERY request
   * - But the server may return stale data for X_QUERY
   *
   * If there are no more messages coming on this channel, the component will get "stuck" and never
   * refetch the latest data until the page is refreshed.
   *
   * This prop lets the calling component tell the wrapper to enqueue a final refetch. (Which means the calling
   * component needs to be able to determine if the data is incomplete or not.)
   */
  hasIncompleteDataShouldRefetch?: boolean;
  disabled?: boolean;
}

function RealtimeUpdateSubscriptionWrapperInternal(
  props: RealtimeUpdateSubscriptionWrapperProps & {apiId: string}
): JSX.Element | null {
  useRealtimeRefetch({
    apiId: props.apiId,
    messageFilter: props.messageFilter,
    refetch: props.refetch,
    refetchOnMessagesFromSameClient: props.refetchOnMessagesFromSameClient,
    hasIncompleteDataShouldRefetch: props.hasIncompleteDataShouldRefetch,
    disabled: props.disabled,
  });

  return props.children;
}

/**
 * Wrapper component that subscribes to realtime updates and triggers refetch.
 *
 * For new code, prefer using the `useRealtimeRefetch` hook directly within your component.
 * This wrapper is maintained for backwards compatibility with existing code.
 */
export default function RealtimeUpdateSubscriptionWrapper(
  props: RealtimeUpdateSubscriptionWrapperProps
): JSX.Element | null {
  return props.apiId ? (
    <RealtimeUpdateSubscriptionWrapperInternal {...props} apiId={props.apiId} />
  ) : (
    props.children
  );
}
